// Dashboard Component for AAR Guest Management System
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { subscribeToTodayCheckIns, subscribeToVolunteerCheckInsForDate, fetchParticipants, fetchAdditionalAttendance } from '../firebase/firestoreService';
import { getCurrentUser } from '../firebase/authService';
import { GUEST_GROUP_ID, VOLUNTEER_GROUP_ID } from '../utils/roles';
import { useNavigate } from 'react-router-dom';
import { calculateDuration, formatDuration } from '../utils/dateUtils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import volunteerPlaceholderAvatar from '../assets/volunteer-avatar.svg';
import AddNoteModal from './AddNoteModal';
import IncidentNotes from './IncidentNotes';
import AdditionalAttendance from './AdditionalAttendance';
import '../styles/Dashboard.css';
import { readOnlyProps } from '../utils/viewAs';

const Dashboard = () => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const [checkIns, setCheckIns] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('checked_in'); // 'all', 'checked_in', 'checked_out'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [additionalAttendance, setAdditionalAttendance] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [outstandingPagers, setOutstandingPagers] = useState([]);
  const [allActiveCheckIns, setAllActiveCheckIns] = useState([]);
  const [activeTab, setActiveTab] = useState('guest'); // 'guest' | 'volunteer'
  const [volunteerCheckIns, setVolunteerCheckIns] = useState([]);
  const [noteGuest, setNoteGuest] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Load both guest (1269) and volunteer (715) participants for image lookups.
    // The volunteer-card avatar resolver (getVolunteerImage) walks the same list.
    const loadParticipants = async () => {
      try {
        const [guests, volunteers] = await Promise.all([
          fetchParticipants(GUEST_GROUP_ID),
          fetchParticipants(VOLUNTEER_GROUP_ID),
        ]);
        const byId = new Map();
        [...guests, ...volunteers].forEach((p) => byId.set(p.id, p));
        setParticipants(Array.from(byId.values()));
      } catch (err) {
        console.log('Could not load participants:', err);
      }
    };
    loadParticipants();

    // Subscribe to check-ins for selected date
    const unsubscribe = subscribeToTodayCheckIns((data) => {
      setCheckIns(data);
      setLoading(false);
    }, selectedDate);

    return () => unsubscribe();
  }, [navigate, selectedDate]);

  // Live-updating clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Subscribe to all currently checked-in guests (all-time, not just today)
  useEffect(() => {
    const checkInsRef = collection(db, 'checkIns');
    const q = query(checkInsRef, where('status', '==', 'checked_in'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach(docSnap => {
        const d = docSnap.data();
        if (!d.guestDeleted) {
          list.push({ id: docSnap.id, ...d });
        }
      });
      list.sort((a, b) => {
        const aTime = a.checkInTime?.toMillis ? a.checkInTime.toMillis() : 0;
        const bTime = b.checkInTime?.toMillis ? b.checkInTime.toMillis() : 0;
        return bTime - aTime;
      });
      setAllActiveCheckIns(list);
    }, (err) => {
      console.error('Error loading active check-ins:', err);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to volunteer check-ins for the selected date
  useEffect(() => {
    const unsubscribe = subscribeToVolunteerCheckInsForDate((data) => {
      setVolunteerCheckIns(data);
    }, selectedDate);
    return () => unsubscribe();
  }, [selectedDate]);

  // Subscribe to outstanding pagers (all-time, not just today)
  useEffect(() => {
    const checkInsRef = collection(db, 'checkIns');
    const q = query(checkInsRef, where('status', '==', 'checked_out'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach(docSnap => {
        const d = docSnap.data();
        if (d.pagerNumber && d.pagerRetrieved !== true && d.guestDeleted !== true) {
          list.push({ id: docSnap.id, ...d });
        }
      });
      list.sort((a, b) => {
        const aTime = a.checkInTime?.toMillis ? a.checkInTime.toMillis() : 0;
        const bTime = b.checkInTime?.toMillis ? b.checkInTime.toMillis() : 0;
        return bTime - aTime;
      });
      setOutstandingPagers(list);
    }, (err) => {
      console.error('Error loading outstanding pagers:', err);
    });
    return () => unsubscribe();
  }, []);

  // Load additional attendance for selected date
  useEffect(() => {
    const loadAdditionalAttendance = async () => {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      const data = await fetchAdditionalAttendance(dateStr);
      const day = selectedDate.getDay();
      const count = day === 0 ? data.sundayCount : day === 2 ? data.tuesdayCount : 0;
      setAdditionalAttendance(count);
    };
    loadAdditionalAttendance();
  }, [selectedDate]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.filter-buttons')) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDatePicker]);

  // Helper function to get participant image
  const getParticipantImage = (checkIn) => {
    const participant = participants.find(p =>
      p.firstName === checkIn.guestFirstName &&
      p.lastName === checkIn.guestLastName
    );
    return participant?.image;
  };

  // When searching, ignore the status filter and show all matching results
  const isSearching = searchTerm.trim() !== '';

  const filteredCheckIns = checkIns.filter(checkIn => {
    // When searching, show all statuses; otherwise filter by selected status
    const statusMatch = isSearching || filter === 'all' || checkIn.status === filter;

    // Filter by search term
    if (!isSearching) return statusMatch;

    const term = searchTerm.toLowerCase();
    const nameMatch = `${checkIn.guestFirstName} ${checkIn.guestLastName}`.toLowerCase().includes(term);
    const pagerMatch = checkIn.pagerNumber && checkIn.pagerNumber.toLowerCase().includes(term);

    return statusMatch && (nameMatch || pagerMatch);
  });

  // Active guests come from the all-time live subscription (so people who checked in
  // on a prior day and never checked out still show up). When searching, apply the
  // search term to that list as well.
  const currentGuests = (() => {
    const base = allActiveCheckIns;
    if (!isSearching) return base;
    const term = searchTerm.toLowerCase();
    return base.filter(c => {
      const nameMatch = `${c.guestFirstName} ${c.guestLastName}`.toLowerCase().includes(term);
      const pagerMatch = c.pagerNumber && c.pagerNumber.toLowerCase().includes(term);
      return nameMatch || pagerMatch;
    });
  })();
  const previousGuests = filteredCheckIns.filter(c => c.status === 'checked_out');

  const formatDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return selectedDate.toLocaleDateString('en-US', options);
  };

  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  const handleDateChange = (e) => {
    // Parse date as local time to avoid timezone issues
    const [year, month, day] = e.target.value.split('-').map(Number);
    const newDate = new Date(year, month - 1, day);
    setSelectedDate(newDate);
    setShowDatePicker(false);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getServiceDay = () => {
    const day = selectedDate.getDay();
    if (day === 0) return 'sunday';
    if (day === 2) return 'tuesday';
    return null;
  };

  // Auto-open date picker when dropdown shows
  useEffect(() => {
    if (showDatePicker) {
      const dateInput = document.querySelector('input[type="date"]');
      if (dateInput && dateInput.showPicker) {
        // Small delay to ensure the input is rendered
        setTimeout(() => {
          try {
            dateInput.showPicker();
          } catch (e) {
            // Silently fail if showPicker is not supported or blocked
          }
        }, 100);
      }
    }
  }, [showDatePicker]);

  // True when an active check-in occurred before midnight of today (i.e. the guest
  // never checked out on a prior day) — used to flag stale active guests.
  const isPriorDayCheckIn = (timestamp) => {
    if (!timestamp) return false;
    const checkInDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return checkInDate < startOfToday;
  };

  // Short date label like "Today", "Yesterday", or "Apr 29" for older check-ins
  const formatCheckInDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateMidnight = new Date(date);
    dateMidnight.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - dateMidnight) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCheckInTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getTimeInBuilding = (checkInTime) => {
    if (!checkInTime) return '';
    const checkIn = checkInTime.toDate ? checkInTime.toDate() : new Date(checkInTime);
    const now = new Date();
    const diff = now - checkIn;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const checkedInCount = allActiveCheckIns.length;
  const checkedOutCount = checkIns.filter(c => c.status === 'checked_out').length;

  // Hourly occupancy series for the selected day (6 AM – 11 PM).
  // Counts guests who were checked in (and not yet checked out) at the top of each hour.
  const occupancyData = useMemo(() => {
    const startHour = 6;
    const endHour = 23;
    const isViewingToday = selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();
    const data = [];
    for (let h = startHour; h <= endHour; h++) {
      const bucket = new Date(selectedDate);
      bucket.setHours(h, 0, 0, 0);
      if (isViewingToday && bucket > now) break;
      let count = 0;
      for (const c of checkIns) {
        if (c.serviceType === 'note' || c.guestDeleted) continue;
        const inT = c.checkInTime?.toDate ? c.checkInTime.toDate() : (c.checkInTime ? new Date(c.checkInTime) : null);
        const outT = c.checkOutTime?.toDate ? c.checkOutTime.toDate() : (c.checkOutTime ? new Date(c.checkOutTime) : null);
        if (!inT) continue;
        if (inT <= bucket && (!outT || outT > bucket)) count++;
      }
      const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      data.push({ hour: label, count });
    }
    return data;
  }, [checkIns, selectedDate]);


  // Volunteer trend: cumulative volunteer check-ins by hour. Volunteer
  // check-ins have no checkout, so we plot a running count of volunteers
  // who have checked in by each hour bucket on the selected day.
  //
  // Range starts ONE hour before the earliest check-in (so there's a
  // visible "step up" baseline) and ends at the current hour (today) or
  // 11 PM (past dates). This avoids padding the chart with hours of zero
  // which made a single-checkin day look empty — a flat 0 line across 6
  // AM..9 AM with a tiny tick at 9 AM is essentially invisible.
  // The last data point is capped at "now" (when viewing today) so it
  // matches the tally tile exactly.
  const volunteerTrendData = useMemo(() => {
    if (!volunteerCheckIns.length) return [];
    const isViewingToday = selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();

    // Find the earliest check-in hour on this day so we can crop leading zeros.
    let earliestHour = 23;
    for (const c of volunteerCheckIns) {
      const inT = c.checkInTime?.toDate ? c.checkInTime.toDate() : (c.checkInTime ? new Date(c.checkInTime) : null);
      if (inT) earliestHour = Math.min(earliestHour, inT.getHours());
    }

    const startHour = Math.max(0, earliestHour - 1); // one zero-baseline hour for visual context
    const endHour = isViewingToday ? Math.min(23, now.getHours()) : 23;

    const data = [];
    for (let h = startHour; h <= endHour; h++) {
      const bucketEnd = new Date(selectedDate);
      bucketEnd.setHours(h, 59, 59, 999);
      const effectiveEnd = isViewingToday && bucketEnd > now ? now : bucketEnd;
      let count = 0;
      for (const c of volunteerCheckIns) {
        const inT = c.checkInTime?.toDate ? c.checkInTime.toDate() : (c.checkInTime ? new Date(c.checkInTime) : null);
        if (inT && inT <= effectiveEnd) count++;
      }
      const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
      data.push({ hour: label, count });
    }
    return data;
  }, [volunteerCheckIns, selectedDate]);

  const peakVolunteers = volunteerTrendData.reduce((max, d) => Math.max(max, d.count), 0);
  const volunteerTrendHasData = volunteerTrendData.some(d => d.count > 0);

  // Look up a volunteer's image from the participants cache.
  const getVolunteerImage = (checkIn) => {
    if (checkIn.volunteerId) {
      const byId = participants.find(p => p.id === checkIn.volunteerId);
      if (byId?.image) return byId.image;
    }
    const byName = participants.find(p =>
      p.firstName === checkIn.volunteerFirstName &&
      p.lastName === checkIn.volunteerLastName
    );
    return byName?.image;
  };

  const formatVolunteerCheckInTime = (timestamp) => {
    if (!timestamp) return '';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading dashboard...</p></div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header-section">
        <div className="dashboard-header-bar">
          <div className="dashboard-titleblock">
            <h1 className="dashboard-title">Dashboard</h1>
            <div className="dashboard-subtitle-line">The Brooklyn Tabernacle</div>
          </div>
          <div className="dashboard-info-cards">
            <div className="info-card info-card-datetime">
              <div className="info-card-icon">📅</div>
              <div className="info-card-body">
                <div className="info-card-label">{formatDate()}</div>
                <div className="info-card-value info-card-value-time">
                  {currentTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                </div>
              </div>
            </div>
            <button
              type="button"
              className={`info-card info-card-clickable${filter === 'checked_in' ? ' active' : ''}${checkedInCount > 0 ? ' flagged' : ''}`}
              title={filter === 'checked_in' ? 'Click to clear filter' : 'Click to show unchecked guests still on site'}
              onClick={() => setFilter(prev => (prev === 'checked_in' ? 'all' : 'checked_in'))}
            >
              <div className="info-card-icon info-card-icon-green">👤</div>
              <div className="info-card-body">
                <div className="info-card-label">Unchecked Guests</div>
                <div className="info-card-value">{checkedInCount}</div>
              </div>
            </button>
            <button
              type="button"
              className={`info-card info-card-clickable${filter === 'outstanding_pagers' ? ' active' : ''}${outstandingPagers.length > 0 ? ' has-alerts' : ''}`}
              title={filter === 'outstanding_pagers' ? 'Click to clear filter' : 'Click to show all outstanding pagers'}
              onClick={() => setFilter(prev => (prev === 'outstanding_pagers' ? 'all' : 'outstanding_pagers'))}
            >
              <div className="info-card-icon info-card-icon-red">📟</div>
              <div className="info-card-body">
                <div className="info-card-label">Outstanding Pagers</div>
                <div className="info-card-value">{outstandingPagers.length}</div>
              </div>
            </button>
          </div>
        </div>
        <button
          className="btn-check-in-dashboard"
          onClick={() => navigate('/check-in')}
        >
          Check In Guest
        </button>
      </div>

      {/* Search and Filters */}
      <div className="search-filter-bar">
        <div className="search-box-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, pager number, or time..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dashboard-search-input"
          />
        </div>
        <div className="filter-buttons">
          <div style={{ position: 'relative' }}>
            <button
              className="btn-today"
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <span>📅</span> Select Date
            </button>
            {showDatePicker && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'white',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 1000
              }}>
                <input
                  type="date"
                  value={`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`}
                  onChange={handleDateChange}
                  max={(() => {
                    const today = new Date();
                    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                  })()}
                  style={{
                    padding: '8px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                {!isToday() && (
                  <button
                    onClick={goToToday}
                    style={{
                      marginTop: '8px',
                      width: '100%',
                      padding: '8px',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Go to Today
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs: Guest / Volunteer */}
      <div className="dashboard-tabs">
        <button
          type="button"
          className={`dashboard-tab${activeTab === 'guest' ? ' active' : ''}`}
          onClick={() => setActiveTab('guest')}
        >
          👤 Guests
        </button>
        <button
          type="button"
          className={`dashboard-tab${activeTab === 'volunteer' ? ' active' : ''}`}
          onClick={() => setActiveTab('volunteer')}
        >
          👥 Volunteers
          {volunteerCheckIns.length > 0 && (
            <span className="dashboard-tab-count">{volunteerCheckIns.length}</span>
          )}
        </button>
      </div>

      {activeTab === 'guest' && <>
      {/* Stats Cards - Now Clickable Filters */}
      <div className="stats-container">
        <div
          className={`stat-card stat-total ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          <div className="stat-icon-wrapper stat-icon-blue">
            <span className="stat-icon">👥</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {getServiceDay() ? checkIns.length + additionalAttendance : checkIns.length}
            </div>
            <div className="stat-label">
              {getServiceDay() === 'sunday' ? 'TOTAL SUNDAY ATTENDANCE' :
               getServiceDay() === 'tuesday' ? 'TOTAL PRAYER MEETING ATTENDANCE' :
               'ALL VISITORS TODAY'}
            </div>
            {getServiceDay() && additionalAttendance > 0 && (
              <div className="stat-breakdown">
                {checkIns.length} checked in + {additionalAttendance} additional
              </div>
            )}
          </div>
        </div>
        <div
          className={`stat-card stat-checked-in ${filter === 'checked_in' ? 'active' : ''}`}
          onClick={() => setFilter('checked_in')}
        >
          <div className="stat-icon-wrapper stat-icon-green">
            <span className="stat-icon">✓</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">{checkedInCount}</div>
            <div className="stat-label">CURRENTLY CHECKED IN</div>
          </div>
        </div>
        <div
          className={`stat-card stat-checked-out ${filter === 'checked_out' ? 'active' : ''}`}
          onClick={() => setFilter('checked_out')}
        >
          <div className="stat-icon-wrapper stat-icon-purple">
            <span className="stat-icon">→</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">{checkedOutCount}</div>
            <div className="stat-label">CHECKED OUT TODAY</div>
          </div>
        </div>
      </div>

      <AdditionalAttendance
        selectedDate={selectedDate}
        checkIns={checkIns}
        occupancyData={occupancyData}
        onTotalsChange={({ total }) => setAdditionalAttendance(total)}
      />

      <IncidentNotes selectedDate={selectedDate} />

      {/* Outstanding Pagers Section (only when that filter is active) */}
      {filter === 'outstanding_pagers' && (
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Outstanding Pagers</h2>
            <span className="section-subtitle">Pagers from past check-outs that were never returned</span>
          </div>
          <div className="guest-list-new">
            {outstandingPagers.length === 0 ? (
              <div className="empty-state">
                <p>No outstanding pagers — all returned. 🎉</p>
              </div>
            ) : (
              outstandingPagers.map((checkIn) => {
                const guestImage = getParticipantImage(checkIn);
                const checkInDate = checkIn.checkInTime?.toDate
                  ? checkIn.checkInTime.toDate()
                  : checkIn.checkInTime ? new Date(checkIn.checkInTime) : null;
                return (
                  <div key={checkIn.id} className="guest-card-new">
                    <div className="guest-name-row">
                      <div className="status-dot-new checked-out"></div>
                      <div className="guest-avatar-new">
                        {guestImage ? (
                          <img src={guestImage} alt={`${checkIn.guestFirstName} ${checkIn.guestLastName}`} />
                        ) : (
                          <img src={placeholderAvatar} alt={`${checkIn.guestFirstName} ${checkIn.guestLastName}`} className="avatar-initials-img" />
                        )}
                      </div>
                      <div className="guest-name-new">
                        {checkIn.guestFirstName} {checkIn.guestLastName}
                      </div>
                    </div>
                    <div className="guest-info-new">
                      <div className="guest-meta">
                        <span className="meta-item">📟 Pager {checkIn.pagerNumber}</span>
                        <span className="meta-item">🕐 Service: {checkIn.serviceType || 'N/A'}</span>
                        {checkInDate && (
                          <span className="meta-item">📅 {checkInDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        )}
                      </div>
                    </div>
                    <div className="guest-time-new">
                      <div className="time-large">{formatCheckInTime(checkIn.checkOutTime)}</div>
                      <div className="time-duration">checked out</div>
                    </div>
                    <div className="guest-actions-new">
                      <button
                        className="btn-view-details"
                        onClick={() => navigate(`/check-out/${checkIn.id}`)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Current Guests Section */}
      {filter !== 'outstanding_pagers' && (isSearching || filter !== 'checked_out') && (isSearching || filter !== 'all' || currentGuests.length > 0) && (
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Current Guests</h2>
          </div>

          <div className="guest-list-new">
              {currentGuests.length === 0 ? (
                <div className="empty-state">
                  <p>No guests currently checked in.</p>
                </div>
              ) : (
                currentGuests.map((checkIn) => {
                  const guestImage = getParticipantImage(checkIn);
                  const stale = isPriorDayCheckIn(checkIn.checkInTime);
                  return (
                    <div key={checkIn.id} className="guest-card-new">
                      <div className="guest-name-row">
                        <div className={`status-dot-new checked-in`}></div>
                        <div
                          className={`guest-avatar-new${stale ? ' stale-active' : ''}`}
                          title={stale ? 'Checked in on a prior day — still active' : undefined}
                        >
                          {guestImage ? (
                            <img src={guestImage} alt={`${checkIn.guestFirstName} ${checkIn.guestLastName}`} />
                          ) : (
                            <img src={placeholderAvatar} alt={`${checkIn.guestFirstName} ${checkIn.guestLastName}`} className="avatar-initials-img" />
                          )}
                        </div>
                        <div className="guest-name-new">
                          {checkIn.guestFirstName} {checkIn.guestLastName}
                        </div>
                      </div>
                      <div className="guest-info-new">
                        <div className="guest-meta">
                          {checkIn.pagerNumber && (
                            <span className="meta-item">📟 Pager {checkIn.pagerNumber}</span>
                          )}
                          <span className="meta-item">🕐 Service: {checkIn.serviceType || 'N/A'}</span>
                          <span className="meta-item">✓ Checked in {formatCheckInDate(checkIn.checkInTime)} at {formatCheckInTime(checkIn.checkInTime)}</span>
                          {checkIn.smsSent && !checkIn.smsViewed && (
                            <span className="meta-item sms-notified-indicator">📲 Notified</span>
                          )}
                          {checkIn.smsViewed && (
                            <span className="meta-item sms-viewed-indicator">👁 Seen</span>
                          )}
                          {checkIn.smsReplyReceived && (
                            <span className="meta-item sms-reply-indicator">💬 Reply received</span>
                          )}
                        </div>
                      </div>
                      <div className="guest-time-new">
                        {!isPriorDayCheckIn(checkIn.checkInTime) ? null : (
                          <div className="time-date-label">{formatCheckInDate(checkIn.checkInTime)}</div>
                        )}
                        <div className="time-large">{formatCheckInTime(checkIn.checkInTime)}</div>
                        <div className="time-duration">In building: {getTimeInBuilding(checkIn.checkInTime)}</div>
                      </div>
                      <div className="guest-actions-new">
                        <button
                          className="btn-checkout"
                          onClick={() => navigate(`/check-out/${checkIn.id}`)}
                        >
                          ↗ Check Out
                        </button>
                        <button
                          className="btn-view-details"
                          onClick={() => navigate(`/check-out/${checkIn.id}`)}
                        >
                          View Details
                        </button>
                        <button
                          className="btn-add-note"
                          onClick={() => setNoteGuest({
                            id: checkIn.guestId,
                            firstName: checkIn.guestFirstName,
                            lastName: checkIn.guestLastName,
                            image: guestImage
                          })}
                          aria-label={`Add note for ${checkIn.guestFirstName} ${checkIn.guestLastName}`}
                          disabled={ro.disabled}
                          title={ro.title}
                        >
                          📝 Add Note
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
          </div>
        </div>
      )}

      {/* Previous Guests Section */}
      {filter !== 'outstanding_pagers' && (isSearching || filter !== 'checked_in') && (
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Previous Guests</h2>
          </div>

          <div className="guest-list-new">
            {previousGuests.length === 0 ? (
              <div className="empty-state">
                <p>No guests checked out today.</p>
              </div>
            ) : (
              previousGuests.map((checkIn) => {
                const guestImage = getParticipantImage(checkIn);
                return (
                  <div key={checkIn.id} className="guest-card-new previous">
                    <div className="guest-name-row">
                      <div className={`status-dot-new checked-out`}></div>
                      <div className="guest-avatar-new">
                        {guestImage ? (
                          <img src={guestImage} alt={`${checkIn.guestFirstName} ${checkIn.guestLastName}`} />
                        ) : (
                          <img src={placeholderAvatar} alt={`${checkIn.guestFirstName} ${checkIn.guestLastName}`} className="avatar-initials-img" />
                        )}
                      </div>
                      <div className="guest-name-new">
                        {checkIn.guestFirstName} {checkIn.guestLastName}
                      </div>
                    </div>
                    <div className="guest-info-new">
                      <div className="guest-meta">
                        {checkIn.pagerNumber && (
                          <span className="meta-item">📟 Pager {checkIn.pagerNumber}</span>
                        )}
                        <span className="meta-item">🕐 Service: {checkIn.serviceType || 'N/A'}</span>
                        <span className="meta-item">✓ Checked out at {formatCheckInTime(checkIn.checkOutTime)}</span>
                      </div>
                    </div>
                    <div className="guest-time-new">
                      <div className="time-large">{formatCheckInTime(checkIn.checkOutTime)}</div>
                      <div className="time-duration">Duration: {formatDuration(calculateDuration(checkIn.checkInTime, checkIn.checkOutTime))}</div>
                    </div>
                    <div className="guest-actions-new">
                      <button
                        className="btn-view-details"
                        onClick={() => navigate(`/check-out/${checkIn.id}`)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      </>}

      {activeTab === 'volunteer' && (
        <>
          <div className="volunteer-summary-row">
            {/* Volunteer tally tile */}
            <div className="stat-card stat-total active volunteer-summary-tile">
              <div className="stat-icon-wrapper stat-icon-blue">
                <span className="stat-icon">👥</span>
              </div>
              <div className="stat-content">
                <div className="stat-number">{volunteerCheckIns.length}</div>
                <div className="stat-label">
                  {isToday() ? 'VOLUNTEERS CHECKED IN TODAY' : `VOLUNTEERS ON ${formatDate().toUpperCase()}`}
                </div>
              </div>
            </div>

            {/* Volunteer Trend Chart */}
            <div className="occupancy-chart-card volunteer-summary-chart">
              <div className="occupancy-chart-header">
                <div>
                  <h3 className="occupancy-chart-title">Volunteer Check-In Trend</h3>
                  <p className="occupancy-chart-subtitle">
                    Cumulative volunteer check-ins throughout {isToday() ? 'today' : formatDate()}
                  </p>
                </div>
                <div className="occupancy-chart-meta">
                  <span className="occupancy-chart-pill">{isToday() ? 'Today' : 'Selected Day'}</span>
                  {peakVolunteers > 0 && (
                    <span className="occupancy-chart-peak">Total: <strong>{peakVolunteers}</strong></span>
                  )}
                </div>
              </div>
              {volunteerTrendHasData ? (
                <div className="occupancy-chart-body">
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={volunteerTrendData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="volunteerFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="hour" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} width={32} />
                      <Tooltip
                        cursor={{ stroke: '#a7f3d0', strokeWidth: 1 }}
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                        formatter={(value) => [`${value} volunteer${value === 1 ? '' : 's'}`, 'Checked in']}
                        labelStyle={{ color: '#111827', fontWeight: 600 }}
                      />
                      <Area type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} fill="url(#volunteerFill)" activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="occupancy-chart-empty">
                  <div className="occupancy-chart-empty-icon">👥</div>
                  <div>No volunteer check-ins for {isToday() ? 'today' : formatDate()} yet.</div>
                </div>
              )}
            </div>
          </div>

          {/* Volunteer list */}
          <div className="section-container">
            <div className="section-header">
              <h2 className="section-title">
                {isToday() ? "Today's Volunteers" : `Volunteers on ${formatDate()}`}
              </h2>
            </div>
            <div className="guest-list-new">
              {volunteerCheckIns.length === 0 ? (
                <div className="empty-state">
                  <p>No volunteers checked in {isToday() ? 'today' : 'on this date'}.</p>
                </div>
              ) : (
                volunteerCheckIns.map((checkIn) => {
                  const volunteerImage = getVolunteerImage(checkIn);
                  const isCheckedOut = !!checkIn.checkOutTime || checkIn.status === 'auto_checked_out' || checkIn.status === 'checked_out';
                  return (
                    <div key={checkIn.id} className={`guest-card-new${isCheckedOut ? ' previous' : ''}`}>
                      <div className="guest-name-row">
                        <div className={`status-dot-new ${isCheckedOut ? 'checked-out' : 'checked-in'}`}></div>
                        <div className="guest-avatar-new">
                          {volunteerImage ? (
                            <img src={volunteerImage} alt={`${checkIn.volunteerFirstName} ${checkIn.volunteerLastName}`} />
                          ) : (
                            <img src={volunteerPlaceholderAvatar} alt={`${checkIn.volunteerFirstName} ${checkIn.volunteerLastName}`} className="avatar-initials-img" />
                          )}
                        </div>
                        <div className="guest-name-new">
                          {checkIn.volunteerFirstName} {checkIn.volunteerLastName}
                        </div>
                      </div>
                      <div className="guest-info-new">
                        <div className="guest-meta">
                          <span className="meta-item">🕐 Service: {checkIn.serviceType || 'N/A'}</span>
                          {checkIn.notes && (
                            <span className="meta-item">📝 {checkIn.notes}</span>
                          )}
                          {checkIn.status === 'auto_checked_out' && (
                            <span className="meta-item">🌙 Auto checked out</span>
                          )}
                        </div>
                      </div>
                      <div className="guest-time-new">
                        <div className="time-large">{formatVolunteerCheckInTime(checkIn.checkInTime)}</div>
                        {isCheckedOut && checkIn.checkOutTime ? (
                          <div className="time-duration">
                            Duration: {formatDuration(calculateDuration(checkIn.checkInTime, checkIn.checkOutTime))}
                          </div>
                        ) : (
                          <div className="time-duration">checked in</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}

      {noteGuest && (
        <AddNoteModal
          guest={noteGuest}
          onClose={() => setNoteGuest(null)}
          onSave={() => setNoteGuest(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
